import { useEffect, useRef } from "react";
import { prefetchResolutionReport } from "./useReportPointer.js";
import { resolutionReadWorthwhile } from "../lib/report/resolutionRead.js";
import { reportFailure } from "../lib/failureCounts.js";

// How long a selection must stick before its report is fetched. Arrowing through a
// list of resolved contracts would otherwise fire a read per contract passed over;
// the effect's cleanup cancels the pending timer on every change, so only a selection
// the user actually rests on is warmed.
const SETTLE_MS = 250;

/**
 * Warm the resolution report when a resolved contract is SELECTED, before the report
 * modal is opened.
 *
 * The read costs ~1s (BigQuery job overhead, not scan time — it can't be optimized in
 * SQL), and it used to be paid with the modal already open and the user waiting on a
 * placeholder. Selection happens seconds earlier — while the timeline is being read —
 * so moving the read there usually hides it completely.
 *
 * A prefetch is never wasted work even if the modal is never opened, which is what
 * makes this worth doing at all: the server caches what it serves onto the contract's
 * rows (server/lib/persistResolutionReport.js), so one prefetch permanently removes
 * that contract's read for EVERY future viewer. Before that write-back existed, an
 * unfollowed prefetch was pure waste and this wasn't a good trade.
 *
 * Gated by the same `resolutionReadWorthwhile` predicate the modal's own read uses, so
 * this can never fetch something the modal wouldn't have — including its
 * already-cached check, which makes the prefetch a no-op once a body is on the wire.
 * Sharing that predicate is deliberate: two gates would drift, and a looser prefetch
 * gate would generate reads the UI has no use for.
 *
 * @param {{
 *   contractKey?: string|null,
 *   contractStatus?: string|null,
 *   resolutionDate?: string|null,   // the contract DEADLINE
 *   storedResolution?: object|null, // active.resolution off the wire
 *   today?: Date|number|null,       // effectiveToday (mock-clock aware)
 *   enabled?: boolean,              // the forecast-tracking release flag
 *   prefetch?: Function,            // injectable for tests
 *   settleMs?: number,
 * }} args
 */
export function useResolutionPrefetch({
  contractKey,
  contractStatus,
  resolutionDate,
  storedResolution,
  today,
  enabled = true,
  prefetch = prefetchResolutionReport,
  settleMs = SETTLE_MS,
} = {}) {
  const worth =
    !!enabled &&
    !!contractKey &&
    resolutionReadWorthwhile({ contractStatus, resolutionDate, today, storedResolution });

  // Latest-value refs for the two injectables, so the timer effect can read them
  // WITHOUT taking them as dependencies.
  //
  // Listing them in deps looks safer and is worse: a caller passing an inline function
  // changes its identity every render, which would re-run the effect and restart the
  // settle timer on each one — the prefetch would then never fire at all. Omitting them
  // outright (what this hook did first) risks the opposite, a stale callback if a caller
  // ever swaps them. The ref avoids both, and removes the need to disable
  // exhaustive-deps: refs are stable, so the linter is satisfied by construction rather
  // than by an assumption about callers.
  const prefetchRef = useRef(prefetch);
  const settleRef = useRef(settleMs);
  useEffect(() => {
    prefetchRef.current = prefetch;
    settleRef.current = settleMs;
  }, [prefetch, settleMs]);

  useEffect(() => {
    if (!worth) return undefined;
    // `worth` folds every input the gate reads, so it and the key are the only real
    // dependencies.
    const t = setTimeout(() => {
      // Catch here rather than relying on the injected implementation to do it — the
      // default already does, but the hook's "best-effort warm-up" contract shouldn't
      // depend on which function it was handed. Covers a synchronous throw AND a
      // rejected promise: either is a lost warm-up, logged and counted, never a crash
      // and never an unhandled rejection.
      try {
        Promise.resolve(prefetchRef.current(contractKey)).catch((err) =>
          reportFailure("resolution_prefetch.failed", err),
        );
      } catch (err) {
        reportFailure("resolution_prefetch.failed", err);
      }
    }, settleRef.current);
    return () => clearTimeout(t);
  }, [worth, contractKey]);
}
